<template>
    <div
        id="country-selector"
        class="relative input-wrapper flex items-center h-fit rounded-none p-0"
        :class="{'border-error':!!validationError}"
    >
        <label
            for="countryInput"
            class="absolute bg-base-100 top-[-11px] left-[12px] pl-1 pr-1 text-xs z-10"
        > Land
        </label>
        <Multiselect
            id="countryInput"
            :model-value="internalValue"
            :internal-search="false"
            :options="filteredCountries"
            placeholder=""
            :clear-on-select="false"
            :show-labels="false"
            preserve-search
            track-by="code"
            class="select-styling"
            @search-change="handleSearch"
            @select="handleSelect"
            @remove="handleRemove"
        >
            <template #noResult>
                Ingen treff
            </template>
            <template #singleLabel="{option}">
                {{ option.name }}
            </template>
            <template #option="{option}">
                <div class="flex items-center gap-4" :class="{'selected-option': option.name == internalValue?.name}">
                    <div>{{ option.name }}</div>
                    <i v-if="option.name == internalValue?.name" class="i-ph-check text-xl text-primary"/>
                </div>
            </template>
        </Multiselect>
    </div>
</template>

<script lang="ts" setup>
import Multiselect from 'vue-multiselect'
import {computed, PropType, ref} from 'vue'
import {Maybe} from '@/types/Maybe'

const props = defineProps(
    {
        isValid: {
            type: Function as PropType<(arg0: Maybe<string>) => string | undefined>,
            required: false
        }
    }
)

const getCountries = (lang: string = 'no'): Country[] => {
    // creds to https://stackoverflow.com/questions/29919596/how-do-i-get-a-list-of-countries-in-my-website
    const A = 65
    const Z = 90
    const countryName = new Intl.DisplayNames([lang], {type: 'region'})
    const countries = []
    for (let i = A; i <= Z; ++i) {
        for (let j = A; j <= Z; ++j) {
            const code = String.fromCharCode(i) + String.fromCharCode(j)
            const name = countryName.of(code)
            if (code !== name && name) {
                countries.push({code, name})
            }
        }
    }
    return countries
}

const countries = getCountries()

const model = defineModel<string | undefined>()

const internalValue = computed<Country | undefined>({
    get: () => {
        return countries.find(c => c.code === model.value)
    },
    set: (country: Country | undefined) => {
        model.value = country?.code
    }
})
const search = ref()

type Country = {
    code: string,
    name: string
}

const handleSearch = (query: string) => {
    if (query.trim().length === 0) {
        search.value = undefined
    } else {
        search.value = query
    }
}

const handleSelect = (country: Country | undefined) => {
    if (country) {
        model.value = country.code
        internalValue.value = country
    }
}

const handleRemove = () => {
    model.value = undefined
    internalValue.value = undefined
}

const filteredCountries = computed(() => {
    if (search.value) {
        return countries.filter(c => c.code.toLowerCase().startsWith(search.value.toLowerCase())
            || c.name.toLowerCase().startsWith(search.value.toLowerCase()))
    } else {
        return countries
    }
})

const validationError = computed(() => {
    if (props.isValid) {
        return props.isValid(model.value)
    }
    return undefined
})

</script>

<style>

</style>
